import HttpService, { ResponseObj } from 'services/http.service';
import { StrapiGiftPageData } from 'types/gift-page';
import { MidasOfferPageData } from 'types/order-join-flow';
import parseUserV2 from 'utils/parseUserV2';

const USE_ESPRESSO_SEGMENTATION =
  process.env.NEXT_PUBLIC_USE_ESPRESSO_SEGMENTATION;

interface FetchStrapiDataArgs {
  mdc?: string;
  userToken?: string;
}

export const getStrapiOrderData = async ({
  mdc,
  userToken,
}: FetchStrapiDataArgs) => {
  const endpoint = USE_ESPRESSO_SEGMENTATION
    ? '/api/espresso/self-service-offer'
    : 'midas/api/v1/self-service-offer';
  const requestLabel = mdc ? `${endpoint}?mdc=${mdc}` : endpoint;
  const response = await HttpService.getInstance().get<MidasOfferPageData>(
    endpoint,
    {
      ...(mdc ? { params: { mdc } } : {}),
      userToken,
    },
  );

  // raises error if response is invalid
  validateResponse(response, requestLabel, userToken);

  // Can return no content/null if the user is not eligible for any offer
  return response.data;
};

export async function getStrapiGiftData(userToken?: string, uid?: string) {
  const url = uid
    ? `/api/espresso/gift-pages/${uid}`
    : '/api/espresso/gift-pages';

  const res = await HttpService.getInstance().get<StrapiGiftPageData[]>(url, {
    userToken,
  });

  if (res.data?.length == 0 || res.status !== 200) {
    throw new Error(
      `Failed to determine offer in strapi from ${url}, ${res?.error}`,
    );
  }

  return res.data;
}

const getUserInfo = (userToken?: string) => {
  const user = parseUserV2(userToken);
  return {
    userId: user.id || null,
    segment: user.segment || 'anonymous',
    email: user.email || null,
  };
};

const validateResponse = <T>(
  response: ResponseObj<T>,
  requestLabel: string,
  userToken?: string,
) => {
  const { userId, segment, email } = getUserInfo(userToken);

  if (response.status === 204) {
    return null;
  }

  if (response.status !== 200) {
    throw new Error(
      `API request failed with status ${response.status} for user ${userId} (segment=${segment}, email=${email}): ${requestLabel}`,
    );
  }

  if (!response?.data) {
    throw new Error(`No data returned from ${requestLabel} (user: ${userId})`);
  }
};
